// components/Sender/SenderApi.js
const BACKEND_ORIGIN       = "https://flautreach.com";
const GLOBAL_SENDER_BASE   = BACKEND_ORIGIN + "/flautreach/api/sender";
const GLOBAL_OUTREACH_BASE = BACKEND_ORIGIN + "/flautreach/api/outreach";
const LOCAL_BASE =
  (typeof window !== "undefined" && window.__SENDER_API_BASE__) ||
  "http://127.0.0.1:17873/api/sender";

export const TOKEN_KEY = "sender_api_token";

function qs(params) {
  if (!params) return "";
  const s = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v == null || v === "" || v === "all") return;
    if (Array.isArray(v)) { if (v.length) s.set(k, v.join(",")); }
    else s.set(k, String(v));
  });
  const str = s.toString();
  return str ? `?${str}` : "";
}

function _authHeaders() {
  const h = { "Accept": "application/json", "Content-Type": "application/json" };
  try {
    const tok = localStorage.getItem(TOKEN_KEY);
    if (tok) h["Authorization"] = `Bearer ${tok}`;
  } catch { /* ignore */ }
  return h;
}

async function request(base, method, path, body) {
  const url = `${base}${path}`;
  const headers = _authHeaders();
  const opts = { method, headers };
  if (body !== undefined) opts.body = JSON.stringify(body);

  let resp;
  try {
    resp = await fetch(url, opts);
  } catch (e) {
    throw new Error(
      `Сеть недоступна (${url}): ${e.message}. ` +
      `Причины: backend не запущен / CORS / VPN.`
    );
  }

  const ct = resp.headers.get("content-type") || "";
  const rawText = await resp.text();
  let data = null, jsonError = null;
  if (rawText) {
    try { data = JSON.parse(rawText); }
    catch (e) { jsonError = e; }
  }

  if (!resp.ok) {
    const detail = (data && typeof data === "object") ? data : {};
    const msg = detail.detail || detail.error || `HTTP ${resp.status}`;
    const err = new Error(msg);
    err.status    = resp.status;
    err.code      = detail.code;
    err.detail    = detail.detail;
    err.payload   = detail;
    err.templates = detail.templates;
    err.raw       = rawText;
    throw err;
  }
  if (resp.status === 204) return null;
  if (!rawText) return null;
  if (jsonError) {
    throw new Error(
      `Сервер вернул не-JSON (content-type=${ct}): ${rawText.slice(0, 300)}`
    );
  }
  return data;
}

const gs = (m, p, b) => request(GLOBAL_SENDER_BASE,   m, p, b);
const go = (m, p, b) => request(GLOBAL_OUTREACH_BASE, m, p, b);
const l  = (m, p, b) => request(LOCAL_BASE,           m, p, b);

export const SenderApi = {
  base: LOCAL_BASE,
  backendOrigin: BACKEND_ORIGIN,
  globalSenderBase: GLOBAL_SENDER_BASE,
  globalOutreachBase: GLOBAL_OUTREACH_BASE,
  tokenKey: TOKEN_KEY,

  // ═════ SEGMENTS ═════
  listSegments:    ()   => gs("GET",    "/segments"),
  createSegment:   (s)  => gs("POST",   "/segments", s),
  updateSegment:   (s)  => gs("PUT",    `/segments/${s.id}`, s),
  deleteSegment:   (id, force) =>
    gs("DELETE", `/segments/${id}${force ? "?force=1" : ""}`),
  bulkDeleteSegments: (ids, force) =>
    gs("POST", "/segments/bulk-delete", { ids, force: !!force }),

  // ═════ TEMPLATES ═════
  listTemplates:   ()   => gs("GET",    "/templates"),
  createTemplate:  (t)  => gs("POST",   "/templates", t),
  updateTemplate:  (t)  => gs("PUT",    `/templates/${t.id}`, t),
  deleteTemplate:  (id) => gs("DELETE", `/templates/${id}`),
  bulkDeleteTemplates: (ids) =>
    gs("POST", "/templates/bulk-delete", { ids }),

  // ═════ RUNTIMES (глобально — метаданные в Django) ═════
  listRuntimes:    ()   => gs("GET",    "/runtimes"),
  createRuntime:   (r)  => gs("POST",   "/runtimes", r),
  updateRuntime:   (r)  => gs("PUT",    `/runtimes/${r.id}`, r),
  deleteRuntime:   (id) => gs("DELETE", `/runtimes/${id}`),

  // ═════ RUNTIMES (локально — управление в daemon) ═════
  listLocalRuntimes:    ()                 => l("GET",    "/runtimes"),
  startLocalRuntime:    (id, kind, config) => l("POST",   `/runtimes/${id}/start`,
                                                { kind, config: config || {} }),
  stopLocalRuntime:     (id)               => l("POST",   `/runtimes/${id}/stop`),
  healthLocalRuntime:   (id)               => l("GET",    `/runtimes/${id}/health`),
  deleteLocalRuntime:   (id)               => l("DELETE", `/runtimes/${id}`),

  // ═════ IMPORT ═════
  importSegments:  (arr) => gs("POST", "/import/segments",  { segments: arr }),
  importTemplates: (arr) => gs("POST", "/import/templates", { templates: arr }),

  // ═════ OUTREACH ═════
  listWorkers:      ()   => go("GET", "/workers/"),
  listEmailAccounts:()   => go("GET", "/email-accounts/"),
  listCampaigns:    ()   => go("GET", "/campaigns/"),
  getWorkerChannels:(id) => go("GET", `/workers/${id}/channels/`),
  filterOptions:    (f)  => go("GET", `/worker/filter-options/${qs(f)}`),
  previewSegment:   (f)  => go("GET", `/worker/filter-options/${qs(f)}`),

  // ═════ LOCAL STATUS / LOGS ═════
  getStatus:          ()        => l("GET",  "/status"),
  stopAll:            ()        => l("POST", "/stop"),
  testSend:           (p)       => l("POST", "/test-email", p),

  getLogs:            (since=0, runId=null, runtimeId=null) => {
    const q = new URLSearchParams();
    q.set("since", String(since));
    if (runId) q.set("run_id", runId);
    if (runtimeId) q.set("runtime_id", runtimeId);
    return l("GET", `/logs?${q.toString()}`);
  },
  getLogsLastRun:     ()        => l("GET", "/logs/last-run"),
  getLogsRuntimes:    (runId)   => l("GET", `/logs/runtimes?run_id=${runId}`),
  deleteAllLogs:      (runId)   => l("DELETE", runId
                                      ? `/logs?run_id=${runId}`
                                      : "/logs"),

  // ═════ LOCAL RUNS ═════
  listLocalRuns:      ()           => l("GET",    "/runs"),
  getLocalRun:        (id)         => l("GET",    `/runs/${id}`),
  createLocalRun:     (p)          => l("POST",   "/runs", p),
  deleteLocalRun:     (id)         => l("DELETE", `/runs/${id}`),
  startLocalRun:      (id, p)      => l("POST",   `/runs/${id}/start`, p || {}),
  stopLocalRun:       (id)         => l("POST",   `/runs/${id}/stop`),
  forceStopLocalRun:  (id)         => l("POST",   `/runs/${id}/force-stop`),
  resetLocalProgress: (id)         => l("POST",   `/runs/${id}/reset-progress`),
  getRunProgress:     (id)         => l("GET",    `/runs/${id}/progress`),
  getRunLogs:         (id, since=0) => l("GET", `/runs/${id}/logs?since=${since}`),
  deleteRunLogs:      (id)         => l("DELETE", `/runs/${id}/logs`),
  getPlanByRunId:     (id)         => l("POST",   `/runs/${id}/plan`),
  getPlan:            (run)        => l("POST",   "/plan", { run }),
  exportWorkerView: (templateId) =>
    gs("POST", `/templates/${encodeURIComponent(templateId)}/export-worker-view`),
    
  // ═════ LOCAL SETTINGS ═════
  getLocalSettings:   ()      => l("GET", "/settings"),
  saveLocalSettings:  (s)     => l("PUT", "/settings", s),
};